// Copied verbatim from dashboard/src/lib/currencies.ts (separate package, no shared imports). Keep in sync.
/**
 * ISO 4217 minor units (client-side mirror).
 *
 * The API has its own copy in `api/src/lib/currencies.ts`; the dashboard is a
 * separate package and cannot import from it, so the table is mirrored here.
 * Keep the two in sync when a currency is added.
 *
 * Only the currencies whose exponent is NOT 2 are listed — everything else
 * falls through to the default of 2. `toFixed(2)` on every currency invents
 * cents for JPY and hides a digit for KWD, which is the bug this table exists
 * to prevent.
 */
const MINOR_UNIT_EXCEPTIONS: Readonly<Record<string, number>> = {
  // Zero-decimal currencies
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  UYI: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  // Three-decimal currencies
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
  // Four-decimal currencies
  CLF: 4,
  UYW: 4,
};

/** USDC has 6 on-chain decimals; 2 is the display precision used everywhere. */
const USDC_DISPLAY_DECIMALS = 2;

export const USDC = 'USDC';

/** Minor-unit exponent for a currency code. Unknown codes default to 2. */
export function minorUnits(currency: string): number {
  const code = currency.trim().toUpperCase();
  if (code === USDC) return USDC_DISPLAY_DECIMALS;
  return MINOR_UNIT_EXCEPTIONS[code] ?? 2;
}

/**
 * Whether `Intl.NumberFormat` will accept this as a currency code. USDC and
 * other non-ISO tickers are rendered as "<amount> <CODE>" instead.
 */
export function isIsoCurrencyCode(currency: string): boolean {
  return /^[A-Za-z]{3}$/.test(currency.trim());
}

/**
 * The fiat currencies the gateway will actually quote and settle — mirrors
 * `SUPPORTED_CURRENCIES` in `api/src/lib/currencies.ts`. Offering a currency
 * the API rejects only produces a failed request after the merchant has filled
 * the form in. Deliberately narrower than the minor-units table above, which
 * must still be able to render historical records in any currency.
 */
export const SUPPORTED_CURRENCIES: readonly string[] = [
  'AED',
  'AUD',
  'BRL',
  'CAD',
  'CHF',
  'CNY',
  'DKK',
  'EUR',
  'GBP',
  'INR',
  'JPY',
  'KES',
  'KRW',
  'MXN',
  'NGN',
  'NOK',
  'NZD',
  'PHP',
  'PLN',
  'SEK',
  'SGD',
  'TRY',
  'USD',
  'ZAR',
];

/** The step a numeric input should use for an amount in this currency. */
export function amountInputStep(currency: string): string {
  const units = minorUnits(currency);
  if (units === 0) return '1';
  return `0.${'0'.repeat(units - 1)}1`;
}
